import { defineStore } from 'pinia'
import { useTokenStore } from './token'
import { useGlobalStore } from './global'
import { normalizeQuotation, normalizeStoreInformationBankFields, parseQuoteFieldsForList } from '../utils/normalizeQuotation'

const jsonFields = ["sale_associate", "products_list", "client_contact", "invoicing_address",
  "delivery_address", "access_information", "store_information", "interior_designer", "delivery_groups"]

// Clés acceptées par l'API (schéma backend) — ne pas envoyer d'autres propriétés (additionalProperties: false)
const quotationApiKeys = [
  'id', 'type', 'country_id', 'store_id', 'user_id', 'reference', 'status', 'date',
  'sale_associate', 'sale_delegate', 'currency_code', 'vat', 'tax_exemption', 'tax_refund',
  'account', 'custom_shipping_handling_rate', 'shipping_handling_rate',
  'additional_shipping_handling_amount', 'additional_deposit_amount', 'global_discount',
  'products_list', 'client_contact', 'invoicing_address', 'delivery_address',
  'access_information', 'project_name', 'store_information', 'interior_designer',
  'delivery_groups', 'signature_date'
]

// Define interfaces for better type safety
interface ProductInList {
  id?: number;
  productId?: number;
  reference: string;
  title: string;
  image?: string;
  quantity: number;
  ttcPrice: number;
  discount: number;
  deliveryGroup: string | null;
  estimatedLeadTime?: string;
}

interface ClientContact {
  name: string;
  client_id: string;
  billing_address: object;
  delivery_address: object;
  first_name?: string;
  last_name?: string;
}

interface StoreInformation {
  country: {
    name: string;
    shipping_rates?: Record<string, number>;
    vat?: number;
    tax_refund?: number;
    code_iso?: string;
    currency?: number;
  };
  deposit: number;
  pdf_footer?: string;
  bank_information?: any;
}

export interface Quotation {
  id?: number;
  reference?: string;
  status: string;
  type: string;
  products_list: ProductInList[];
  account: boolean;
  delivery_groups: { name: string; date: string }[];
  client_contact: ClientContact;
  interior_designer: { name: string; email?: string; phone?: string };
  project_name?: string;
  language_quote_po?: string;
  store_information: StoreInformation;
  tax_refund: number;
  sale_associate?: { name: string; phone: string; email: string };
  sale_delegate?: string;
  sale_delegate_phone?: string;
  sale_delegate_email?: string;
  user_id?: number;
  country_id?: number;
  store_id?: number;
  vat?: number;
  shipping_handling_rate?: number;
  currency_code?: string;
  global_discount?: number;
  additional_shipping_handling_amount?: number;
  additional_deposit_amount?: number;
  tax_exemption?: boolean;
  custom_shipping_handling_rate?: boolean;
}


export const useQuotationsStore = defineStore({

  id: 'quotations-store',

  state: () => ({
    quotations: {} as { data?: Quotation[] },
    emptyQuotation: {
      status: "DRAFT",
      type: "",
      products_list: [],
      account: true,
      delivery_groups: [{ name: "A", date: "" }, { name: "B", date: "" }, { name: "C", date: "" }],
      client_contact: { name: "", client_id: "", billing_address: {}, delivery_address: {} },
      interior_designer: { name: "", email: "", phone: "" },
      project_name: "",
      language_quote_po: "",
      store_information: { country: { "name": "" }, deposit: 50 },
      sale_associate: { name: "", phone: "", email: "" },
      tax_refund: 0,
    } as Quotation,
    currentQuotation: {} as Quotation,
    currentQuotationReference: "",
    isSaving: false // Protection contre les sauvegardes concurrentes
  }),

  actions: {
    _ensureSaleAssociateDefaults(quote: any) {
      parseQuoteFieldsForList(quote);
    },

    _normalizeStoreInformationBankFields(si: any) {
      normalizeStoreInformationBankFields(si);
    },

    _parseQuoteFields(quote: any) {
      return parseQuoteFieldsForList(quote);
    },

    async fetchQuotations(force = false) {
      const globalStore = useGlobalStore()
      const tokenStore = useTokenStore()

      if (force || !this.quotations?.hasOwnProperty("data")) {
        // Utiliser $fetch au lieu de useFetch pour les appels après le montage
        const quotations = await $fetch<{ data: any[] }>('/quotation', {
          method: 'GET',
          headers: { "Authorization": "Bearer " + tokenStore.currentValue },
          baseURL: globalStore.apiBase
        })

        quotations.data.map((quote: any) => this._parseQuoteFields(quote));
        this.quotations = quotations as { data: Quotation[] };
      }
    },

    async initializeCurrentQuotation(type: string) {
      const globalStore = useGlobalStore()

      this.currentQuotation = JSON.parse(JSON.stringify(this.emptyQuotation))
      this.currentQuotation.type = type;

      await globalStore.fetchStores(false)
      await globalStore.fetchCountries(false)
      await globalStore.fetchUsers(false)
      await globalStore.fetchCurrencies(false)

      if (globalStore.user) {
        const user = globalStore.user as any; // Loosen type to fix compile errors
        this.currentQuotation.sale_associate = {
          "name": (user.first_name + " " + user.last_name),
          "phone": user.phone,
          "email": user.email
        };

        this.currentQuotation.user_id = user.id
        this.currentQuotation.country_id = user.country_id
        this.currentQuotation.store_id = user.store_id
      }

      if (globalStore.user && globalStore.user.store_id) {
        const userStore = globalStore.stores.data.find(it => it.id == globalStore.user.store_id)
        if (userStore) {
          this.currentQuotation.store_information = JSON.parse(JSON.stringify(userStore));
        }
      }

      if (globalStore.user && globalStore.user.country_id) {
        let userCountry = globalStore.countries.data.find(it => it.id == globalStore.user.country_id) as any; // Loosen type

        if (userCountry) {
          this.currentQuotation.store_information.country = JSON.parse(JSON.stringify(userCountry));
          this.currentQuotation.vat = userCountry.vat
          this.currentQuotation.tax_refund = 0
          const shipping_rates = this.currentQuotation.store_information.country.shipping_rates || {}

          this.currentQuotation.shipping_handling_rate = Math.min.apply(
            null,
            Object.values(shipping_rates).length > 0 ? Object.values(shipping_rates) : [0]
          )

          if (userCountry.currency) {
            this.currentQuotation.currency_code = globalStore.getCurrencyCodeById(userCountry.currency);
          }
        }

        // Orfevrerie fonctionne comme mobilier
        this.currentQuotation.account = this.currentQuotation.type === "mobilier" || this.currentQuotation.type === "orfevrerie"
      }

      if (this.currentQuotation.store_information) {
        this._normalizeStoreInformationBankFields(this.currentQuotation.store_information);
      }

      this.currentQuotationReference = JSON.stringify(this.currentQuotation);

    },

    setCurrentQuotation(id: number, opts?: { force?: boolean }) {

      const globalStore = useGlobalStore()

      // Sans force : même id = on garde le brouillon (navigation Infos / Produits / Contact).
      // Avec force : reprise depuis le cache liste (retour depuis /quote, autre devis, etc.).
      if (!opts?.force && id === this.currentQuotation.id) {
        return
      }

      const foundQuotation = this.quotations.data?.find(elt => elt.id == id)
      if (!foundQuotation) return;
      // Clone pour ne pas muter l’entrée du cache liste tant que ce n’est pas sauvegardé
      this.currentQuotation = JSON.parse(JSON.stringify(foundQuotation)) as Quotation;

      // S'assurer que le devis a un statut valide
      if (!this.currentQuotation.status) {
        console.warn(`Quotation ${id} has no status, setting to DRAFT`);
        this.currentQuotation.status = "DRAFT";
      }

      normalizeQuotation(this.currentQuotation);

      Object.keys(this.emptyQuotation).forEach((key) => {
        const k = key as keyof Quotation;
        if (!this.currentQuotation.hasOwnProperty(k) || (this.currentQuotation as any)[k] == null) {
          (this.currentQuotation as any)[k] = JSON.parse(JSON.stringify(this.emptyQuotation[k]));
        }

        var subobj = this.emptyQuotation[k]

        if (typeof subobj === 'object' && subobj !== null && this.currentQuotation[k]) {
          Object.keys(subobj).forEach(k2 => {
            if (!(this.currentQuotation[k] as any)!.hasOwnProperty(k2)) {
              ((this.currentQuotation[k] as any)!)[k2] = JSON.parse(JSON.stringify((subobj as any)[k2]))
            }
          })

        }
        this.currentQuotationReference = JSON.stringify(this.currentQuotation);

      })

      if (!this.currentQuotation.store_information.country
        || !this.currentQuotation.store_information.country.name
        || !this.currentQuotation.store_information.country.code_iso) {
        let userCountry = globalStore.countries.data.find(it => it.id == globalStore.user.country_id)
        if (userCountry) {
          this.currentQuotation.store_information.country = JSON.parse(JSON.stringify(userCountry));
        }
      }

      if ((!this.currentQuotation.store_information.country
        || !this.currentQuotation.store_information.country.name) && !this.currentQuotation.store_information.country.tax_refund) {
        const userCountry = globalStore.countries.data.find(it => it.id == globalStore.user.country_id) as any;
        if (userCountry) {
          this.currentQuotation.store_information.country.tax_refund = userCountry.tax_refund;
        }
      }

      if (!this.currentQuotation.store_information.pdf_footer) {
        const userStore = globalStore.stores.data.find(it => it.id == globalStore.user.store_id) as any;
        if (userStore) {
          this.currentQuotation.store_information.pdf_footer = userStore.pdf_footer;
        }
      }

      this._normalizeStoreInformationBankFields(this.currentQuotation.store_information);
    },

    /**
     * Extrait le message d'erreur lisible depuis une erreur API
     */
    _extractErrorMessage(error: any): string {
      if (!error) return 'Erreur inconnue';

      // Erreur Feathers avec message
      if (error.data?.message) return error.data.message;
      if (error.message) return error.message;

      // Erreur HTTP avec statusMessage
      if (error.statusMessage) return error.statusMessage;

      // Erreur avec response
      if (error.response?._data?.message) return error.response._data.message;

      // Fallback
      return typeof error === 'string' ? error : 'Erreur lors de la sauvegarde';
    },

    /**
     * Sauvegarde le devis courant
     * @returns { success: boolean, id?: number, error?: string }
     */
    async saveCurrentQuotation(): Promise<{ success: boolean; id?: number; error?: string }> {
      // Protection contre les sauvegardes concurrentes
      if (this.isSaving) {
        console.warn('Une sauvegarde est déjà en cours, opération annulée');
        return { success: false, error: 'Une sauvegarde est déjà en cours' };
      }

      this.isSaving = true;

      try {
        const nuxtApp = useNuxtApp()
        const raw = this.currentQuotation as any
        if (raw.store_information && typeof raw.store_information === 'object') {
          this._normalizeStoreInformationBankFields(raw.store_information);
        }
        const quote: any = {}

        quotationApiKeys.forEach(key => {
          if (raw[key] === undefined || raw[key] === null) return
          if (jsonFields.indexOf(key) === -1) {
            quote[key] = raw[key]
          } else {
            quote[key] = JSON.stringify(raw[key])
          }
        })

        if (quote.id) {
          const { data, error } = await nuxtApp.$patch("/quotation", quote.id, quote)
          if (error.value || !data.value?.id) {
            const errorMessage = this._extractErrorMessage(error.value);
            return { success: false, error: errorMessage };
          } else {
            this.currentQuotationReference = JSON.stringify(this.currentQuotation);
            const idx = this.quotations.data?.findIndex(elt => elt.id === data.value.id) ?? -1
            if (idx >= 0 && this.quotations.data) {
              this.quotations.data[idx] = JSON.parse(JSON.stringify(this.currentQuotation)) as Quotation
            }
          }
          return { success: true, id: data.value.id };
        } else {
          let clientDate = new Date()
          const offset = clientDate.getTimezoneOffset()
          clientDate = new Date(clientDate.getTime() - (offset * 60 * 1000))
          quote.reference = "Q" + clientDate.toISOString().split('T')[0].replaceAll("-", "") + "-"

          const { data, error } = await nuxtApp.$post("/quotation", quote)

          if (error.value || !data.value?.id) {
            const errorMessage = this._extractErrorMessage(error.value);
            return { success: false, error: errorMessage };
          } else {
            const newQuote = this._parseQuoteFields(data.value);
            // Set the server-assigned ID and reference back onto currentQuotation
            (this.currentQuotation as any).id = newQuote.id;
            (this.currentQuotation as any).reference = newQuote.reference;
            this.currentQuotationReference = JSON.stringify(this.currentQuotation);
            // Add the new quote to the local list to prevent race conditions
            if (this.quotations.data) {
              this.quotations.data.push(newQuote);
            } else {
              this.quotations.data = [newQuote];
            }
          }
          return { success: true, id: data.value.id };
        }
      } finally {
        this.isSaving = false;
      }
    },

    hasCurrentQuotationChanged() {

      return JSON.stringify(this.currentQuotation) != this.currentQuotationReference
    },

    discardUnsavedChanges() {
      if (!this.currentQuotationReference) return
      try {
        this.currentQuotation = JSON.parse(this.currentQuotationReference) as Quotation
      } catch {
        /* ignore */
      }
    },

    computeCurrentQuotationTotals() {
      if (!this.currentQuotation || !this.currentQuotation.products_list) {
        return {}
      }
      return this.computeTotals(this.currentQuotation);
    },

    computeTotals(q: Quotation | null | undefined) {
      if (!q) {
        return {
          number_of_articles: 0,
          articles_net_price: 0,
          articles_ttc_price: 0,
          articles_discount: 0,
          pro_discount: 0,
          tax_refund: 0,
          global_discount: 0,
          estimated_shipping_handling: 0,
          deposit: 0,
          total_all_inc: 0,
          max_lead_time_in_months: 0,
          max_lead_time_by_group: { A: 0, B: 0, C: 0 },
        };
      }

      const vatRate = q.vat ? q.vat / 100 : 0;
      const globalDiscount = q.global_discount ? q.global_discount / 100 : 0;

      let numberOfArticles = 0;
      let articlesTtcPrice = 0;
      let articlesHtPrice = 0;
      let itemDiscount = 0;
      let articlesDiscount = 0;
      let professionalPrice = 0;
      let taxRefund = 0;
      let totalDiscounts = 0;
      let totalVATInclBeforeDiscounts = 0;
      let totalVATInclAfterDiscounts = 0;
      let allLeadTimesInMonths: number[] = [];
      let maxLeadTimeByGroup: Record<string, number> = { A: 0, B: 0, C: 0 };

      if (Array.isArray(q.products_list)) {
        q.products_list.forEach(item => {
          const quantity = Number(item?.quantity) || 0;
          const unitTtc = Number(item?.ttcPrice) || 0;
          numberOfArticles += quantity;

          const itemTtc = unitTtc * quantity;
          const itemHt = vatRate > -1 ? itemTtc / (1 + vatRate) : itemTtc;

          articlesTtcPrice += itemTtc;
          articlesHtPrice += itemHt;

          totalVATInclBeforeDiscounts += itemTtc;

          // Calculate item-specific discount

          itemDiscount = item.discount ? (q.tax_exemption ? itemHt : itemTtc) * item.discount / 100 : 0;

          totalDiscounts += itemDiscount;
          articlesDiscount += itemDiscount;

          totalVATInclAfterDiscounts = totalVATInclBeforeDiscounts - totalDiscounts;

          // All lead times in months
          let estimatedLeadTime = parseInt(item.estimatedLeadTime || '0'); // Ensure it's in days.
          let monthsEstimatedLeadTime = Math.ceil(estimatedLeadTime / 20);
          allLeadTimesInMonths.push(monthsEstimatedLeadTime);

          // Max lead time per group
          const deliveryGroupKey = item.deliveryGroup ?? 'A';
          if (!maxLeadTimeByGroup[deliveryGroupKey] || monthsEstimatedLeadTime > maxLeadTimeByGroup[deliveryGroupKey]) {
            maxLeadTimeByGroup[deliveryGroupKey] = monthsEstimatedLeadTime;
          }
        });
      }

      // Professional Price calculation
      if (q.tax_exemption) {
        professionalPrice = articlesHtPrice * globalDiscount;
      } else {
        professionalPrice = totalVATInclAfterDiscounts * globalDiscount;
      }
      totalDiscounts += professionalPrice;

      // Total VAT Inclusive Price After Discounts
      let totalVATIncl;
      if (q.tax_exemption) {
        totalVATIncl = articlesHtPrice - totalDiscounts;
      } else {
        totalVATIncl = totalVATInclBeforeDiscounts - totalDiscounts;
      }
      taxRefund = totalVATIncl * ((Number(q.tax_refund) || 0) / 100);
      totalVATIncl -= taxRefund;

      // Estimated Shipping & Handling
      // Montant fixe uniquement si « Personnaliser » est activé ; sinon % (matrice pays ou taux saisi).
      // Sans ça, avec la personnalisation désactivée, le pays met à jour shipping_handling_rate mais le total restait à 0.
      let estimatedShipping = 0;

      const addShipRaw = q.additional_shipping_handling_amount
      const useFixedCustomAmount =
        q.custom_shipping_handling_rate &&
        addShipRaw !== null &&
        addShipRaw !== undefined &&
        String(addShipRaw).trim() !== '';

      // LTH : pas de frais au % (matrice pays) ; montant fixe possible si « Personnaliser » + saisie.
      if (useFixedCustomAmount) {
        estimatedShipping = Number(q.additional_shipping_handling_amount) || 0;
      } else if (q.type !== 'lth' && q.shipping_handling_rate) {
        estimatedShipping =
          articlesHtPrice * (Number(q.shipping_handling_rate) / 100);
      }

      // Total All Inclusive
      const totalAllInc = totalVATIncl + (estimatedShipping || 0);

      // deposit
      let deposit = q.additional_deposit_amount;

      if (deposit === 0) {
        deposit = 0;
      } else if (deposit == null || deposit === '') {
        const storeDepositRate = Number(q.store_information?.deposit);
        const depositRate = Number.isFinite(storeDepositRate) ? storeDepositRate : 50;
        deposit = totalAllInc * (depositRate / 100);
      } else {
        deposit = Number(deposit) || 0;
      }

      // Find the maximum in the array to get the maximum lead time.
      let maxLeadTimeInMonths = Math.max(...allLeadTimesInMonths);

      return {
        "number_of_articles": numberOfArticles,
        "articles_net_price": articlesHtPrice,
        "articles_ttc_price": articlesTtcPrice,
        "articles_discount": articlesDiscount,
        "pro_discount": professionalPrice,
        "tax_refund": taxRefund,
        "global_discount": totalDiscounts,
        "estimated_shipping_handling": estimatedShipping,
        "deposit": deposit,
        "total_all_inc": totalAllInc,
        "max_lead_time_in_months": maxLeadTimeInMonths === -Infinity ? 0 : maxLeadTimeInMonths,
        "max_lead_time_by_group": maxLeadTimeByGroup,
      };
    }

  },
})
